import { CurrencyPipe, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    DefaultUrlSerializer,
    PreloadAllModules,
    provideRouter,
    UrlSerializer,
    UrlTree,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { Ability, PureAbility } from '@casl/ability';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { environment } from 'environments/environment';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { firstValueFrom } from 'rxjs';
import { provideAuthInitializer } from './core/initializers/auth.initializer';
import { provideGlobalStores } from './core/providers/provide-global-stores';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { XsrfInterceptor } from './helpers/xsrf.interceptor';
import { RoutingService } from './services/routing.service';

// Custom Serializer to stop Angular from stripping the trailing slash
export class TrailingSlashUrlSerializer extends DefaultUrlSerializer {
    override serialize(tree: UrlTree): string {
        const url = super.serialize(tree);
        // If the original URL had a slash, ensure the serialized one does too
        if (window.location.pathname.endsWith('/') && !url.endsWith('/')) {
            return url + '/';
        }
        return url;
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(
            withXsrfConfiguration({
                cookieName: 'XSRF-TOKEN', // default is 'XSRF-TOKEN'
                headerName: 'X-XSRF-TOKEN', // default is 'X-XSRF-TOKEN'
            }),
            withInterceptorsFromDi()
        ),
        { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
        provideRouter(appRoutes, withPreloading(PreloadAllModules), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
        { provide: UrlSerializer, useClass: TrailingSlashUrlSerializer },

        provideAppInitializer(() => {
            const routingService = inject(RoutingService);
        }),

        [DatePipe],
        [CurrencyPipe],
        { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'dd/MM/yyyy',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Permission Based Config
        { provide: Ability, useValue: new Ability() },
        { provide: PureAbility, useExisting: Ability },
        importProvidersFrom(RecaptchaV3Module),
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.recaptcha.siteKey,
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classic',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        provideAuthInitializer(),
        provideGlobalStores(),
    ],
};
